import React from "react";
import { useToast } from "@chakra-ui/react";
import { queryCache } from "react-query";
import { useRouter } from "next/dist/client/router";
import { useRequest } from "../contexts/request";
import getVideoId from "get-video-id";
import { t } from "@lingui/macro";
import { getTiktokVideoId, getInstagramVideoId } from "../utils";

const useDonation = () => {
  const { post } = useRequest();
  const router = useRouter();

  const toast = useToast();

  const createDonation = async (
    streamerId,
    {
      from,
      email,
      phone,
      giphy,
      mediaType,
      yt,
      soundboard,
      ytStart,
      tiktok,
      reels,
      voice_note,
      image_guess,
      image_guess_answer,
      ...formValues
    },
    wishlistId
  ) => {
    const { payment_type } = formValues;
    let options = {};
    if (mediaType === "gif" && giphy) {
      options.media = {
        type: "giphy",
        id: giphy.id,
      };
    } else if (mediaType === "yt" && yt) {
      const { id } = getVideoId(yt);

      options.media = {
        type: "yt",
        id,
        start: ytStart,
      };
    } else if (mediaType === "tiktok" && tiktok) {
      const { id } = await getTiktokVideoId(tiktok);

      options.media = {
        type: "tiktok",
        id,
      };
    } else if (mediaType === "reels" && reels) {
      const id = getInstagramVideoId(reels);
      options.media = {
        type: "reels",
        id,
      };
    } else if (mediaType === "voice_note" && voice_note) {
      options.media = {
        type: "voice_note",
        src: voice_note,
      };
    } else if (mediaType === "sb" && soundboard) {
      options.media = {
        type: "sb",
        id: soundboard.id,
      };
    } else if (mediaType === "image_guess" && image_guess) {
      options.media = {
        type: "image_guess",
        img_guess_path: image_guess,
        answer: image_guess_answer,
      };
    }
    try {
      const payloadJson = {
        ...formValues,
        ...options,
        customer_info: { first_name: from, email, phone },
      };

      if (wishlistId) {
        payloadJson.wishlist_id = wishlistId;
      }

      let donationPath = `donations/${streamerId}`;
      if (["linkaja", "ovo", "dana", "qris", "bca", "mandiri", "bni", "bri", "permata", "bjb", "bsi", "cimb"].includes(payment_type)) {
        donationPath = `donations/snap/${streamerId}`;
      }
      const { json } = await post(donationPath, {
        json: payloadJson,
        // set timeout to 1 minute, because sometimes we need download ig reels first
        timeout: 60000
      });

      if (payment_type === "qris") {
        const {
          data: { id },
        } = json;
        router.push({
          pathname: `/qris/snap/${id}`,
        });
        return;
      }

      if (payment_type === "wechat_pay") {
        const {
          data
        } = json;
        const { payment_id } = data;
        router.push({
          pathname: `/wechat/${payment_id}`,
        });
        return;
      }
      // Tell user to open ovo
      if (payment_type === "ovo") {
        router.push("/open-ovo");
        return;
      }

      // If linkaja, gopay, or dana go to url
      else if (
        [
          "linkaja",
          "dana",
          "gopay",
          "shopeepay",
          "gcash",
          "paymaya",
          "grabpay",
          "alipay",
          "inter_cc",
          "linepay",
          "paypay",
          "rakutenpay",
          "merpay",
        ].includes(payment_type)
      ) {
        const {
          data: { redirect_url: redirectUrl },
        } = json;

        if (redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }
      }

      else if (
        ["bca", "bni", "bri", "permata", "mandiri", "bjb", "bsi", "cimb"].includes(payment_type)
      ) {
        const {
          data: { id },
        } = json;
        router.push({
          pathname: `/va/snap/${id}`,
        });
        return;
      }

      throw new Error("Something is not right with our payment gateway");
    } catch (error) {
      let d = "";
      let title = t`Transaksi Gagal`;
      if (!error.response) {
        d =
          t`Ada yang salah dari sistem kami / penyedia layanan pembayaran. Cobalah beberapa saat lagi.`;

      } else {
        if (error.response.status === 401) {
          toast({
            title: t`Transaksi Gagal`,
            description: t`Silahkan login terlebih dahulu.`,
            status: "warning",
            duration: 10000,
            isClosable: true,
          });
          return;
        }
        switch (error.response.json.message) {
          // OVO error message
          case "PHONE_NUMBER_NOT_REGISTERED":
            d = t`Nomor tidak terdaftar di sistem pembayaran OVO. Silahkan mencoba lagi dengan nomor yang lain.`;
            break;
          case "USER_DID_NOT_AUTHORIZE_THE_PAYMENT":
            d =
              t`User tidak mengkonfirmasi transaksi dalam batas waktu yang diberikan. Silahkan mencoba lagi.`;
            break;
          case "USER_DECLINED_THE_TRANSACTION":
            d = t`User membatalkan transaksi.`;
            break;
          case "WISHLIST_NOT_FOUND": // Wishlist error message
            d = t`Wishlist tidak ditemukan`;
            break;
          case "WISHLIST_CLOSED":
            d = t`Wishlist lagi nggak bisa didukung, coba lain kali ya!`;
            break;
          case "DONATION_AMOUNT_IS_LESS_THAN_WISHLIST_PRICE":
            d = t`Nominal kurang dari harga wishlist`;
            break;
          case "WISHLIST_ALREADY_PAID":
            d = t`Wishlist sudah didukung, coba wishlist lain ya!`;
            break;
          case "WISHLIST_AWAITING_PAYMENT":
            d = t`Lagi nunggu dibayar pendukung lain nih, coba lagi nanti ya!`;
            break;
          case "MABAR_MIN_DONATION_INVALID":
            d = t`Min jumlah dukungan untuk mabar tidak sesuai`;
            break;
          case "MABAR_NOT_AVAILABLE":
            d = t`Mabar yang kamu pilih tidak tersedia saat ini, coba lagi nanti ya!`;
            break;
          case "CHALLENGE_NOT_FOUND":
            // probably just deactivated: refetch so the stale card disappears
            queryCache.invalidateQueries(["streamer-public", router.query.username]);
            d = t`Challenge yang kamu pilih sudah tidak tersedia, pilih challenge lain ya!`;
            break;
          case "CHALLENGE_MIN_DONATION_INVALID":
            d = t`Min jumlah dukungan untuk challenge tidak sesuai`;
            break;
          case "CHALLENGE_CLOSED":
            d = t`Challenge tidak bisa digabung dengan media share, voice note, soundboard, tebak gambar, mabar, atau wishlist`;
            break;
          case "IG_REELS_RATE_LIMITED":
            title = t`Media Share Reels Sedang Bermasalah`;
            d = t`Gagal memproses Reels karena batas Instagram. Silakan coba lagi dalam 5-10 menit.`;
            break;
          case "BANK_NOT_AVAILABLE":
            d = t`Moda pembayaran sedang tidak tersedia, silahkan pilih moda pembayaran yang lain.`;
            toast({
              title: t`Transaksi Gagal`,
              description: d,
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            setTimeout(() => {
              window.location.reload();
            }, 3000);
            throw new Error(d);
          default:
            d =
              t`Ada yang salah dari sistem kami / penyedia pembayaran. Cobalah beberapa saat lagi.`;
        }
      }

      toast({
        title: title,
        description: d,
        status: "error",
        duration: 10000,
        isClosable: true,
      });
      throw new Error(d);
    }
  };
  const calculateAmountToPay = async (
    streamerName,
    { from, email, phone, ...formValues },
  ) => {
    try {
      const { json } = await post(`donations/${streamerName}/calculate_pg_amount`, {
        json: {
          ...formValues,
          customer_info: { first_name: from, email, phone },
        },
      });
      return json.data
    } catch (error) {
      toast({
        title: "",
        description: t`Sedang terjadi error, coba pilih metode pembayaran kembali!`,
        status: "error",
        duration: 10000,
        isClosable: true,
      });
    }
  };

  const calculateAmountToPayWishlist = async (
    streamerName,
    { from, email, phone, ...formValues },
  ) => {
    try {
      const { json } = await post(`donations/${streamerName}/calculate_wishlist_amount`, {
        json: {
          ...formValues,
          customer_info: { first_name: from, email, phone },
        },
      });
      return json.data
    } catch (error) {
      toast({
        title: "",
        description: t`Sedang terjadi error, coba pilih metode pembayaran kembali!`,
        status: "error",
        duration: 10000,
        isClosable: true,
      });
    }
  };

  return { createDonation, calculateAmountToPay, calculateAmountToPayWishlist };
};
export default useDonation;
